import React, { useCallback, useEffect, useState } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// hooks
import { useNavigate } from 'react-router-dom';

// Actions
import { deleteTemplate, getAllTemplates, getViewProof } from '~/redux/actions/template-builder';
import { failure, success } from '~/redux/actions/snackbar-actions';

// utils
import { HAS_ENVELOPE } from '~/utils/constants';
import { createEnvelopeProof } from '~/utils/template-builder';
import { MESSAGES } from '~/utils/message';

// Components
import ThumbnailModal from './ThumbnailModal';
import DuplicateTemplateModal from '../DuplicateModal';
import ConfirmModal from '~/components/General/ConfirmModal';
import Loader from '~/components/General/Loader';
import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcTablePagination from '~/components/General/OlcTablePagination';

// MUI Components
import {
  Box,
  IconButton,
  Typography,
  MenuItem,
  CircularProgress,
} from '@mui/material';

// icons
import MoreVertIcon from '@mui/icons-material/MoreVert';

// assets
import {
  ContactDetailsDeleteCIcon,
  DownloadTemplateCIcon,
  DuplicateTemplateCIcon,
  TemplateCopyCIcon,
  TemplateDeleteCIcon,
  TemplateZoomCIcon,
  TrashTemplateCIcon,
} from '~/assets/images';

// styles
import './styles.scss';

const GridView = (props) => {
  // props
  const {
    templates,
    selectedTemplateIds,
    searchApplied,
    filterSize,
    totalTemplatesInDB,
    isLoading,
  } = props;

  // dispatch
  const dispatch = useDispatch();

  // navigate
  const navigate = useNavigate();

  // states
  const [selectedRecord, setSelectedRecord] = useState({});
  const [imageLoading, setImageLoading] = useState({
    value: true,
    id: null,
  });
  const [showBackThumbnail, setShowBackThumbnail] = useState({
    value: false,
    id: null,
  });
  const [isShowDeleteModel, setIsShowDeleteModel] = useState({
    open: false,
    type: 'delete',
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const [downloadingProof, setDownloadingProof] = useState({
    status: false,
    rowId: null,
  });
  const [openThumbnailModal, setOpenThumbnailModal] = useState({
    open: false,
    id: activeRow,
  });

  const [downloadEnvelope, setDownloadingEnvelope] = useState({
    status: false,
    rowId: null,
  });

  const [openConfirmModal, setOpenConfirmModal] = useState({
    type: '',
    open: false,
    message: '',
    handleOk: () => {},
    loading: false,
    header: '',
    successButtonName: '',
    icon: '',
    svg: '',
    className: '',
    modalName: '',
    handleClose: () => {},
  });

  const open = Boolean(anchorEl);
  const handleClick = (event, template) => {
    event.stopPropagation();
    setActiveRow(template); // Set active row ID when the menu is opened
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  // selectors
  const search = useSelector((state) => state.templateReducer.search);
  const searchProductIds = useSelector((state) => state.templateReducer.searchProductIds);
  const searchCreator = useSelector((state) => state.templateReducer.searchCreator);
  const sortBy = useSelector((state) => state.templateReducer.sortBy);
  const sortOrder = useSelector((state) => state.templateReducer.sortOrder);

  // functions
  const getAllTemplatesFromServer = useCallback(
    (page, perPage) => {
      dispatch(
        getAllTemplates(
          page,
          perPage,
          search,
          searchProductIds,
          searchCreator,
          '',
          selectedTemplateIds,
          true,
          false,
          true,
          sortBy,
          sortOrder
        )
      );
    },
    [dispatch, search, searchCreator, searchProductIds, selectedTemplateIds, sortBy, sortOrder]
  );

  const handleImageLoad = (id) => {
    setImageLoading((prev) => ({
      ...prev,
      value: false,
      id: id,
    }));
  };

  const handleShowBackThumbnail = (value, id) => {
    setShowBackThumbnail((prev) => ({
      ...prev,
      value: value,
      id: id,
    }));
    setImageLoading((prev) => ({
      ...prev,
      value: true,
      id: id,
    }));
  };

  const handleChangePage = (event, page) => {
    getAllTemplatesFromServer(page, templates.perPage);
  };

  const handleDuplicateDialog = (record) => {
    setIsShowDeleteModel((prev) => ({
      ...prev,
      open: true,
      type: 'duplicate',
    }));
    setSelectedRecord(record);
  };

  const handleShowDeleteDialog = (record) => {
    setOpenConfirmModal({
      type: 'delete',
      open: true,
      header: 'Delete Template',
      message: 'Are you sure you want to delete this Template?',
      description:
        'It will remain associated with existing orders but won’t be available for new orders.',
      handleOk: () => handleDelete(record),
      successButtonName: 'Yes',
      className: 'deleteTemplateModal',
      icon: ContactDetailsDeleteCIcon,
      handleClose: () => setOpenConfirmModal((prev) => ({ ...prev, open: false })),
    });
  };

  const handleDelete = async (record) => {
    setOpenConfirmModal((prev) => ({ ...prev, loading: true }));
    const response = await deleteTemplate(record.id);
    if (response.status === 200) {
      dispatch(success(response.data.message));
      getAllTemplatesFromServer(templates.currentPage - 1, templates.perPage);
      setOpenConfirmModal((prev) => ({ ...prev, loading: false, open: false }));
    } else {
      dispatch(failure(response.data.message));
    }
  };

  const createViewProof = async (title, id, type) => {
    try {
      setDownloadingProof({
        status: true,
        id: id,
      });
      const response = await getViewProof(id, type);
      const binaryData = atob(response.data.data.base64Pdf);

      if (response.status === 200) {
        // Create a Uint8Array from the binary data
        const uint8Array = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          uint8Array[i] = binaryData.charCodeAt(i);
        }

        // Create a Blob from the Uint8Array
        const blob = new Blob([uint8Array], { type: 'application/pdf' });

        // Create an Object URL for the Blob
        const url = URL.createObjectURL(blob);
        downloadPDF(title, url);
        dispatch(success('Mailer Proof generated successfully'));
        handleClose();
      } else {
        dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
      }
    } catch (error) {
      dispatch(failure(error?.message || MESSAGES.VIEW_PROOF_ERROR));
    } finally {
      setDownloadingProof({
        status: false,
        id: null,
      });
    }
  };

  const handleEditNavigate = useCallback(
    (id, templateType) => {
      if (templateType === 'json') {
        navigate(`/template-builder/${id}`);
      } else if (templateType === 'html_v2') {
        navigate(`/html-template-builder/${id}`);
      } else {
        navigate(`/template-html/${id}`);
      }
    },
    [navigate]
  );

  const handleRowClick = (params, event) => {
    event.stopPropagation();
    handleEditNavigate(params.id, params.templateType);
  };

  const handleKeyboardAction = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      action();
    }
  };

  const downloadEnvelopeProof = async (title, id) => {
    try {
      setDownloadingEnvelope({
        status: true,
        id: id,
      });
      const payload = {
        templateId: id,
      };
      await dispatch(createEnvelopeProof(title, payload));
    } catch (error) {
      console.log(error);
      dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
    } finally {
      setDownloadingEnvelope({
        status: false,
        id: null,
      });
    }
  };

  const downloadPDF = (title, url) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = title + '.pdf';

    // Append the link to the document
    document.body.appendChild(link);

    // Trigger a click on the link
    link.click();

    // Remove the link from the document
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (!isLoading && templates.rows.length === 0 && templates.currentPage > 1) {
      getAllTemplatesFromServer(templates.currentPage - 1, templates.perPage);
    }
  }, [
    getAllTemplatesFromServer,
    isLoading,
    templates.currentPage,
    templates.perPage,
    templates.rows,
  ]);

  if (isLoading) {
    return (
      <Box className="templateGridLoader">
        <Loader />
      </Box>
    );
  }

  return (
    <>
      <Box className="templateCardWrapper">
        {templates.rows.map((template) => (
          <Box
            className="templateCard"
            key={template.id}
            role="link"
            tabIndex={0}
            aria-label={`Edit template ${template.title}`}
            onClick={(e) => handleRowClick(template, e)}
            onKeyDown={(e) =>
              handleKeyboardAction(e, () => handleEditNavigate(template.id, template.templateType))
            }
          >
            {/* loading image */}
            {imageLoading.value && imageLoading.id === template?.id && (
              <Box className="imageLoadingWrapper">
                <CircularProgress className="imageLoadingProgress" />
              </Box>
            )}
            {/* image */}
            <img
              src={
                showBackThumbnail.value && showBackThumbnail?.id === template?.id
                  ? template?.backThumbnailUrl
                  : template?.thumbnailUrl
              }
              alt="template"
              onLoad={() => handleImageLoad(template?.id)}
              style={{
                display: imageLoading.value && imageLoading.id === template?.id ? 'none' : 'block',
              }}
            />
            {/* template zoom icon */}
            <img
              src={TemplateZoomCIcon}
              alt=""
              className="templateZoomIcon"
              role="button"
              tabIndex={0}
              aria-label={`Preview template ${template.title}`}
              onClick={(e) => {
                e.stopPropagation();
                setOpenThumbnailModal({ open: true, id: template?.id });
              }}
              onKeyDown={(e) =>
                handleKeyboardAction(e, () =>
                  setOpenThumbnailModal({ open: true, id: template?.id })
                )
              }
            />
            {/* template card content */}
            <Box className="templateCardContent">
              <Typography>{template?.title}</Typography>
              <IconButton
                aria-label={`Open actions for template ${template.title}`}
                onClick={(event) => handleClick(event, template)}
                className="moreIcon"
              >
                <MoreVertIcon />
              </IconButton>
            </Box>
            {/* thumbnail icons */}
            {template?.backThumbnailUrl && (
              <>
                <Box
                  className={`leftArrow ${!(showBackThumbnail.value && showBackThumbnail?.id === template?.id) ? 'disabled' : ''}`}
                  role="button"
                  tabIndex={
                    showBackThumbnail.value && showBackThumbnail?.id === template?.id ? 0 : -1
                  }
                  aria-label={`Show front of template ${template.title}`}
                  aria-disabled={
                    !(showBackThumbnail.value && showBackThumbnail?.id === template?.id)
                  }
                  onClick={
                    showBackThumbnail.value && showBackThumbnail?.id === template?.id
                      ? (e) => {
                          e.stopPropagation();
                          handleShowBackThumbnail(false, template?.id);
                        }
                      : undefined
                  }
                  onKeyDown={
                    showBackThumbnail.value && showBackThumbnail?.id === template?.id
                      ? (e) =>
                          handleKeyboardAction(e, () =>
                            handleShowBackThumbnail(false, template?.id)
                          )
                      : undefined
                  }
                />
                <Box
                  className={`rightArrow ${showBackThumbnail.value && showBackThumbnail?.id === template?.id ? 'disabled' : ''}`}
                  role="button"
                  tabIndex={
                    !(showBackThumbnail.value && showBackThumbnail?.id === template?.id) ? 0 : -1
                  }
                  aria-label={`Show back of template ${template.title}`}
                  aria-disabled={showBackThumbnail.value && showBackThumbnail?.id === template?.id}
                  onClick={
                    !(showBackThumbnail.value && showBackThumbnail?.id === template?.id)
                      ? (e) => {
                          e.stopPropagation();
                          handleShowBackThumbnail(true, template?.id);
                        }
                      : undefined
                  }
                  onKeyDown={
                    !(showBackThumbnail.value && showBackThumbnail?.id === template?.id)
                      ? (e) =>
                          handleKeyboardAction(e, () => handleShowBackThumbnail(true, template?.id))
                      : undefined
                  }
                />
              </>
            )}
          </Box>
        ))}
      </Box>
      {!isLoading &&
        (searchApplied || filterSize > 0 ? templates.count >= 10 : totalTemplatesInDB > 10) && (
          <OlcTablePagination
            count={Math.ceil(templates.count / templates.perPage)}
            page={templates.currentPage || 1}
            pageSize={templates.perPage}
            onPageChange={handleChangePage}
            onPageSizeChange={(event) => getAllTemplatesFromServer(1, event.target.value)}
            from={templates.from}
            to={templates.to}
            total={templates.count}
          />
        )}
      <OlcActionMenu
        id="long-menu"
        MenuListProps={{
          'aria-labelledby': 'long-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock
      >
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            handleDuplicateDialog(activeRow);
            handleClose();
          }}
        >
          <img src={DuplicateTemplateCIcon} alt="duplicate" />
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            createViewProof(activeRow?.title, activeRow?.id, activeRow?.templateType);
          }}
          disabled={downloadingProof.status && downloadingProof.id === activeRow?.id}
        >
          {downloadingProof.status && downloadingProof.id === activeRow?.id ? (
            <CircularProgress
              sx={{
                color: '#ed5c2f !important',
                width: '19px !important',
                height: '19px !important',
              }}
            />
          ) : (
            <img src={DownloadTemplateCIcon} alt="mailer proof" />
          )}
          {downloadingProof.status && downloadingProof.id === activeRow?.id
            ? 'Downloading...'
            : 'Mailer Proof'}
        </MenuItem>
        {HAS_ENVELOPE.includes(+activeRow?.product?.id) && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              downloadEnvelopeProof(activeRow?.title, activeRow?.id);
            }}
            disabled={downloadEnvelope.status && downloadEnvelope.id === activeRow?.id}
          >
            {downloadEnvelope.status && downloadEnvelope.id === activeRow?.id ? (
              <CircularProgress
                sx={{
                  color: '#ed5c2f !important',
                  width: '19px !important',
                  height: '19px !important',
                }}
              />
            ) : (
              <img src={DownloadTemplateCIcon} alt="envelope proof" />
            )}
            {downloadEnvelope.status && downloadEnvelope.id === activeRow?.id
              ? 'Downloading...'
              : 'Envelope Proof'}
          </MenuItem>
        )}
        <MenuItem
          onClick={(event) => {
            event.stopPropagation();
            handleShowDeleteDialog(activeRow);
            handleClose();
          }}
        >
          <img src={TrashTemplateCIcon} alt="Delete" />
          Delete
        </MenuItem>
      </OlcActionMenu>
      {isShowDeleteModel.open && isShowDeleteModel.type === 'duplicate' && (
        <DuplicateTemplateModal
          open={isShowDeleteModel.open}
          handleClose={setIsShowDeleteModel}
          selectedRecord={selectedRecord}
        />
      )}

      <ConfirmModal openConfirmModal={openConfirmModal} setOpenConfirmModal={setOpenConfirmModal} />

      <ThumbnailModal
        templates={templates}
        openThumbnailModal={openThumbnailModal}
        onClose={() => setOpenThumbnailModal({ open: false, id: null })}
      />
    </>
  );
};

export default GridView;
